import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Stack, IconButton, CircularProgress, Typography, Button } from '@mui/material'
import { Add, Remove } from '@mui/icons-material'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import { formatTemperature } from '../utils/string'
import { AuroraSetMinTemperatureLimitActionInput } from '../types/AuroraAwhpActionInput'
import PinCodeDialog from './PinCodeDialog'
import useDialog from '../hooks/useDialog'
import { AuroraAwhpActionNack } from '../errors'
import AuroraControl from './AuroraControl'

export interface AuroraMinTemperatureLimitControlProps {
  value: number | null
  loading?: boolean
  disabled?: boolean
  onSubmit: (input: AuroraSetMinTemperatureLimitActionInput) => Promise<void>
}

export default function AuroraMinTemperatureLimitControl({
  value,
  loading = false,
  disabled = false,
  onSubmit
}: AuroraMinTemperatureLimitControlProps) {
  const { t } = useTranslation('aurora')

  const { open, openDialog, closeDialog } = useDialog()

  const [minTempLimit, setMinTempLimit] = useState<number | null>(null)

  function handleIncrement() {
    setMinTempLimit((prev) => {
      const current = prev !== null ? prev : value
      if (current !== null) {
        return Math.ceil(current * 2) / 2 + 0.5
      }
      return null
    })
  }

  function handleDecrement() {
    setMinTempLimit((prev) => {
      const current = prev !== null ? prev : value
      if (current !== null) {
        return Math.ceil(current * 2) / 2 - 0.5
      }
      return null
    })
  }

  async function handleSubmit(pin: number) {
    closeDialog()

    if (minTempLimit === null) {
      return
    }

    try {
      await onSubmit({
        alarms: {
          pin,
          minTempLimit
        }
      })

      setMinTempLimit(null)
    } catch (error) {
      if (error instanceof AuroraAwhpActionNack) {
        switch (error.message) {
          case 'PIN_MISSING':
          case 'PIN_REJECTED':
            openDialog()
            break
        }
      }
    }
  }

  function handleReset() {
    setMinTempLimit(null)
  }

  return (
    <>
      <AuroraControl
        direction="column"
        label={t('minTemperatureLimit')}
        loading={loading}
        disabled={disabled}
        control={
          loading ? (
            <CircularProgress size={24} sx={{ my: 2 }} />
          ) : (
            <Stack direction="column" justifyContent="center" alignItems="center" gap={2} pt={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" gap={3}>
                <IconButton
                  color={minTempLimit !== null ? 'primary' : 'inherit'}
                  disabled={loading || disabled}
                  size="small"
                  sx={{ border: '1px solid' }}
                  onClick={handleDecrement}
                >
                  <Remove />
                </IconButton>

                {minTempLimit !== null ? (
                  <Typography variant="h2" color={loading ? 'text.disabled' : 'primary'}>
                    {formatTemperature(minTempLimit)}
                  </Typography>
                ) : (
                  <Typography variant="h2" color="text.primary">
                    {formatTemperature(value)}
                  </Typography>
                )}

                <IconButton
                  color={minTempLimit !== null ? 'primary' : 'inherit'}
                  disabled={loading || disabled}
                  size="small"
                  sx={{
                    border: '1px solid'
                  }}
                  onClick={handleIncrement}
                >
                  <Add />
                </IconButton>
              </Stack>

              {minTempLimit !== null && (
                <Stack direction="row" alignItems="center" gap={2}>
                  <LoadingButton
                    variant="outlined"
                    color="primary"
                    size="small"
                    loading={loading}
                    disabled={disabled}
                    onClick={openDialog}
                  >
                    {t('actions:confirm')}
                  </LoadingButton>

                  {!loading && (
                    <Button
                      variant="text"
                      color="inherit"
                      size="small"
                      disabled={disabled}
                      onClick={handleReset}
                    >
                      {t('actions:cancel')}
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          )
        }
      />

      <PinCodeDialog open={open} onClose={closeDialog} onSubmit={handleSubmit} />
    </>
  )
}
